import React, { Suspense } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
// TODO: HOLY MOLY, we are importing from the analytics project....

import { useMatomoTrackEvent } from "../../../../../analytics/useMatomoTrackEvent";
import { useFaultEvent } from "../../../../../api/useFaultEvents";
import { useProject, type Project } from "../../../../../api/useProjects";
import { toPath } from "../../../../../router/helpers/path";
import BlankStateV2 from "@ui/BlankStateV2";
import tUI from "@ui/i18n";
import Loading from "@ui/Loading";
import { useFaultEventAccess } from "../../hooks/useFaultEventAccess";
import FaultEvent from "./FaultEvent";
import ManualFaultEvent from "./ManualFaultEvent";
import type { Event } from "../../../../../api/useEvents";
import { useManualFaultEvent } from "../../../../../api/useManualFaultEvent";

export interface EventOutletContext {
  event: Event;
  project: Project;
  onCloseEvent?: () => void;
}

export const EventPage = React.memo(() => {
  const { manualFaultEventId } = useParams();
  return (
    <Suspense fallback={<Loading />}>
      {manualFaultEventId ? <ManualFaultEventPage /> : <FaultEventPage />}
    </Suspense>
  );
});

const FaultEventPage = React.memo(() => {
  const { projectId, faultEventId } = useParams();
  if (!projectId) throw new Error("no project");
  if (!faultEventId) throw new Error("no fault event id");

  const hasFaultEventAccess = useFaultEventAccess();
  const trackEvent = useMatomoTrackEvent();
  trackEvent({
    category: "Events",
    action: "Navigate to Fault Events page",
    name: "Open Fault Events page",
  });
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const project = useProject(projectId);
  const faultEvent = useFaultEvent(projectId, faultEventId);

  if (!hasFaultEventAccess) {
    return <Navigate to={toPath("/projects/:projectId", { projectId })} replace />;
  }

  if (project === undefined || faultEvent === undefined)
    return (
      <BlankStateV2
        url={pathname}
        subtitle={tUI("404.description")}
        onClick={() => navigate("")}
        buttonLabel={tUI("404.action")}
      />
    );

  return (
    <FaultEvent
      project={project}
      event={{
        ...faultEvent,
        type: "DIAGNOSED",
      }}
    />
  );
});

const ManualFaultEventPage = React.memo(() => {
  const { projectId, manualFaultEventId } = useParams();
  if (!projectId) throw new Error("no project");
  if (!manualFaultEventId) throw new Error("no manualFaultEventId");

  const { pathname } = useLocation();
  const hasFaultEventAccess = useFaultEventAccess();
  const navigate = useNavigate();
  const project = useProject(projectId);
  const { data: manualFaultEvent } = useManualFaultEvent(projectId, manualFaultEventId);

  if (!hasFaultEventAccess) {
    return <Navigate to={toPath("/projects/:projectId", { projectId })} replace />;
  }

  if (project === undefined || manualFaultEvent === undefined)
    return (
      <BlankStateV2
        url={pathname}
        subtitle={tUI("404.description")}
        onClick={() => navigate("")}
        buttonLabel={tUI("404.action")}
      />
    );

  return (
    <ManualFaultEvent
      project={project}
      event={{
        ...manualFaultEvent,
        failureNames: [manualFaultEvent.metadata.manualFault.name],
        type: "MANUAL",
      }}
    />
  );
});
